// CR561: Deal Card Types as cards for Admin/Live tabs
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DEAL_TYPE_REGISTRY, type DealCategory } from "@/lib/deals/dealTypeRegistry";
import { CheckCircle2, XCircle } from "lucide-react";

interface DealCardTypesTableProps {
  mode: "admin" | "live";
}

const LIVE_VISIBLE: Set<DealCategory> = new Set(["BONUS", "DISCOUNT", "POINTS", "IN_KIND"]);

export function DealCardTypesTable({ mode }: DealCardTypesTableProps) {
  const entries = Object.entries(DEAL_TYPE_REGISTRY) as [DealCategory, typeof DEAL_TYPE_REGISTRY[DealCategory]][];
  const filtered = mode === "live" ? entries.filter(([key]) => LIVE_VISIBLE.has(key)) : entries;

  if (filtered.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          No deal card types available yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground">
        {mode === "admin" ? "All Deal Card Types" : "Live-Visible Deal Card Types"}
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map(([key, config]) => {
          const Icon = config.icon;
          const isLive = LIVE_VISIBLE.has(key);
          return (
            <Card key={key} className="transition-colors hover:border-primary/30">
              <CardHeader className="pb-2 pt-4 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`rounded-md p-1.5 ${config.badgeColors.bg}`}>
                      <Icon className={`h-4 w-4 ${config.badgeColors.text}`} />
                    </div>
                    <CardTitle className="text-sm font-semibold">{config.label}</CardTitle>
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${config.badgeColors.bg} ${config.badgeColors.text} ${config.badgeColors.border}`}
                  >
                    {config.themeKey}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-1">
                <p className="text-xs font-mono text-muted-foreground mb-2">{key}</p>
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                    Admin
                  </span>
                  <span className="flex items-center gap-1">
                    {isLive
                      ? <><CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> Live Enabled</>
                      : <><XCircle className="h-3.5 w-3.5 text-muted-foreground/40" /> Admin Only</>
                    }
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
